import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plane, ArrowRight, CheckCircle, Clock, MapPin, IndianRupee } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { tier1Cities, getRouteData, allRoutes } from '@/data/routes';

export default function RoutePage() {
  const { routeSlug } = useParams();
  const parts = routeSlug?.split('-to-') || [];
  const fromSlug = parts[0];
  const toSlug = parts[1];

  const fromCity = tier1Cities.find(c => c.slug === fromSlug);
  const toCity = tier1Cities.find(c => c.slug === toSlug);

  if (!fromCity || !toCity) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-2xl font-bold text-slate-900">Route not found</h1>
          <Link to="/" className="text-amber-600 mt-4 inline-block">Go Home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const pricing = getRouteData(fromSlug, toSlug);
  const savings = pricing.regular - pricing.airvia;
  const savingsPercent = Math.round((savings / pricing.regular) * 100);
  const annualSavings12 = savings * 12;
  const annualSavings24 = savings * 24;

  const reverseSlug = `${toSlug}-to-${fromSlug}`;
  const relatedRoutes = allRoutes
    .filter(r => (r.from.slug === fromSlug || r.to.slug === toSlug) && r.slug !== routeSlug)
    .slice(0, 4);

  const pageTitle = `Cheap ${fromCity.name} to ${toCity.name} Flights - Save ${savingsPercent}% | AirVia`;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-16 bg-slate-900" data-testid="route-hero">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <p className="text-amber-500 font-medium mb-3">Flight Subscription Route</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4" data-testid="route-title">
              {fromCity.name} to {toCity.name} <span className="text-amber-400">Flights</span>
            </h1>
            <p className="text-lg text-slate-300 mb-2">
              {fromCity.code} → {toCity.code} &middot; {pricing.distance} &middot; {pricing.duration}
            </p>
            <p className="text-xl text-green-400 font-semibold">Save up to {savingsPercent}% with AirVia subscription</p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Comparison */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-10">
            {fromCity.name} to {toCity.name} Flight Price Comparison
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="p-8 border-slate-200">
              <p className="text-sm text-slate-500 uppercase font-medium mb-2">Regular Booking</p>
              <p className="text-4xl font-bold text-slate-900 mb-1">₹{pricing.regular.toLocaleString()}</p>
              <p className="text-slate-500 text-sm mb-6">Average one-way price</p>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-start gap-2"><span className="text-red-400 mt-0.5">✕</span> Prices change daily</li>
                <li className="flex items-start gap-2"><span className="text-red-400 mt-0.5">✕</span> 2-3x surge during holidays</li>
                <li className="flex items-start gap-2"><span className="text-red-400 mt-0.5">✕</span> Last-minute = expensive</li>
                <li className="flex items-start gap-2"><span className="text-red-400 mt-0.5">✕</span> No cost predictability</li>
              </ul>
            </Card>
            <Card className="p-8 border-amber-300 bg-amber-50 ring-2 ring-amber-400" data-testid="airvia-price-card">
              <p className="text-sm text-amber-700 uppercase font-medium mb-2">AirVia Subscription</p>
              <p className="text-4xl font-bold text-slate-900 mb-1">₹{pricing.airvia.toLocaleString()}</p>
              <p className="text-amber-700 text-sm mb-6">Flat rate per ticket</p>
              <ul className="space-y-3 text-sm text-slate-700">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> Same price every day</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> No surge pricing ever</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> Book anytime, same rate</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> Save ₹{savings.toLocaleString()} per flight</li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Savings Calculator */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-10">
            Your Annual Savings on {fromCity.name}-{toCity.name}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { label: 'Per Flight', value: `₹${savings.toLocaleString()}`, sub: 'saved per ticket' },
              { label: 'Monthly Flyer', value: `₹${annualSavings12.toLocaleString()}`, sub: '12 flights/year' },
              { label: 'Bi-Monthly Flyer', value: `₹${annualSavings24.toLocaleString()}`, sub: '24 flights/year' },
            ].map((item, i) => (
              <Card key={i} className="p-6 border-slate-200">
                <p className="text-sm text-slate-500 mb-1">{item.label}</p>
                <p className="text-3xl font-bold text-green-600">{item.value}</p>
                <p className="text-xs text-slate-400 mt-1">{item.sub}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Get Flat ₹{pricing.airvia.toLocaleString()} on {fromCity.name}-{toCity.name} Flights
          </h2>
          <p className="text-slate-300 mb-8">Tell us your requirements and receive a customized plan within 24 hours</p>
          <Link to="/subscription-plans" data-testid="route-cta-btn">
            <Button className="rounded-full bg-amber-600 hover:bg-amber-700 text-white px-10 py-6 text-lg font-semibold">
              Get Your Custom Plan <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Related Routes */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Related Routes</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to={`/flights/${reverseSlug}`} key="reverse">
              <Card className="p-4 border-slate-200 hover:border-amber-400 transition-colors cursor-pointer">
                <p className="font-semibold text-slate-900">{toCity.name} → {fromCity.name}</p>
                <p className="text-sm text-green-600">₹{pricing.airvia.toLocaleString()} flat</p>
              </Card>
            </Link>
            {relatedRoutes.map(route => (
              <Link to={`/flights/${route.slug}`} key={route.slug}>
                <Card className="p-4 border-slate-200 hover:border-amber-400 transition-colors cursor-pointer">
                  <p className="font-semibold text-slate-900">{route.from.name} → {route.to.name}</p>
                  <p className="text-sm text-green-600">₹{route.pricing.airvia.toLocaleString()} flat</p>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
